/**
 * <XtsStopPositionMovers> — render a static ghost mover at every stop
 * position of every enabled station belonging to the active XPU.
 *
 * Toggled by `display.showStopPositionMovers`. Visual options come from
 * `display.stopPositionMoverOptions`. Ghosts use the XPU's `moverType`
 * for their GLB and tint to the station colour by default.
 *
 * Architecture:
 *   - Mounted per-XPU inside `<XtsScene>`, mirroring `<XtsStations>`.
 *   - Each ghost is a fully decoupled `<XtsStaticMover>` — independent
 *     GLB instance, no useFrame, no MoverPositionStore subscription, no
 *     selection / hover / collision participation.
 */

import React from 'react';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import { resolveStopPositionMm } from '../../geometry/positionFrame.js';
import { argbIntToHex } from './XtsStations.js';
import { XtsStaticMover } from './XtsStaticMover.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type {
  MoverType3D,
  PartTransformation,
  PositionFrame,
  StationConfig,
  StopPositionMoverOptions,
} from '../../types.js';

interface Props {
  stations: StationConfig[];
  chainsByPartOid: Map<number, BuiltChain>;
  /** Per-part transforms keyed by `PartConfig.globalNumber`. */
  partTransformsByPartOid?: Map<number, PartTransformation | undefined>;
  moverType: MoverType3D;
  glbUrl: string;
  assetsBaseUrl: string;
  options?: StopPositionMoverOptions;
  /** XPU position frame applied to each stop position. */
  positionFrame?: PositionFrame;
}

const XtsStopPositionMoversImpl: React.FC<Props> = ({
  stations,
  chainsByPartOid,
  partTransformsByPartOid,
  moverType,
  glbUrl,
  assetsBaseUrl,
  options,
  positionFrame,
}) => {
  const opacity = options?.opacity ?? 0.45;
  const tintByStation = options?.tintByStation !== false;

  return (
    <group name="xts-stop-position-movers">
      {stations
        .filter((s) => s.isEnabled)
        .flatMap((station) =>
          (station.stopPositions ?? []).map((stopMm, i) => {
            // Stop positions reference the FIRST partOid in the
            // station — same convention as the stop-marker rendering
            // in <XtsStations>. Multi-part stations dropping stops
            // mid-way is out of scope for the v1 ghost overlay.
            const partOid = station.partOids[0];
            if (partOid === undefined) return null;
            const chain = chainsByPartOid.get(partOid);
            if (!chain) return null;
            const tint = tintByStation
              ? argbIntToHex(station.stationColor)
              : undefined;
            // Honour `stopPositionsRelativeTo` + the XPU positionFrame
            // so ghosts always land on the same chain spot as the
            // diamond / cone markers rendered by <XtsStations>.
            const chainPos = resolveStopPositionMm(
              stopMm,
              station,
              positionFrame,
            );
            return (
              <PartTransformGroup
                key={`s${station.stationId}-${i}`}
                name={`stop-mover-wrap-${station.stationId}-${i}`}
                partTransformation={partTransformsByPartOid?.get(partOid)}
              >
                <XtsStaticMover
                  chain={chain}
                  partPositionMm={chainPos}
                  glbUrl={glbUrl}
                  moverType={moverType}
                  assetsBaseUrl={assetsBaseUrl}
                  opacity={opacity}
                  {...(tint ? { tintColor: tint } : {})}
                />
              </PartTransformGroup>
            );
          }),
        )}
    </group>
  );
};

export const XtsStopPositionMovers = React.memo(XtsStopPositionMoversImpl);
XtsStopPositionMovers.displayName = 'XtsStopPositionMovers';
